import cn from 'classnames';
import { useStoreAvailability } from '@utils/store/useStoreAvailabilityNew';
import { useUI } from '@context/GlobalUIContext';
import { usePdp } from '@context/PdpContext';
import { useStoresUI } from '@context/StoresContext';
import { parseAvailabilityMessage } from '@utils/parseAvailabilityMessage';
import { useLocale } from '@hooks/useLocale';
import { useSpuItemAvailabilityVariant } from '@hooks/useSpuItemAvailabilityVariant';
import Skeleton from 'react-loading-skeleton';
import SimpleButton from '_components/UI/SimpleButton';
import { FEATURE_FLAG_DISABLE_SPU } from '@config/feature-flag';
import FulfillmentStockLevelRow from '../FulfillmentStockLevelRow';

import styles from './StoreAvailabilityMessage.module.scss';

// Splits a leading "." or "," off the text after a link. It goes inside the
// link: a button wraps as one box, so punctuation after it can drop onto a
// line of its own, as in FR "Choisissez un autre magasin."
const splitLeadingPunctuation = (text = '') => {
  const [, punctuation = '', rest = ''] = /^([.,]?)\s*(.*)$/s.exec(text) ?? [];
  return { punctuation, rest };
};

export interface StoreAvailabilityMessageProps {
  storeId: string;
  pickupMessage?: string;
  selectedAvailability: boolean;
}

const StoreAvailabilityMessage = ({
  storeId,
  pickupMessage,
  selectedAvailability,
}: StoreAvailabilityMessageProps) => {
  const { openStockLevelModal } = useUI();
  const { selectedVariant, shippingRestrictions, quantity } = usePdp();
  const { favouriteStoreDetails, setLocationAndFavouriteStore } = useStoresUI();
  const locale = useLocale();
  const result = useStoreAvailability(
    storeId,
    pickupMessage ?? null,
    locale ?? 'en',
    quantity ?? null
  );

  const { data } = useSpuItemAvailabilityVariant({
    selectedVariant: selectedVariant,
    productName: 'Product Name',
    shippingRestrictions: shippingRestrictions,
    quantity: quantity ?? null,
  });

  const availabilityMessageForStore = data?.find(
    (item) => item.store.data.mec_store_id === storeId
  );

  // Parse the availability message for clickable store name
  const parsedMessage = availabilityMessageForStore?.message?.short
    ? parseAvailabilityMessage(
        availabilityMessageForStore.message.short,
        locale
      )
    : null;
  const secondLine = splitLeadingPunctuation(parsedMessage?.secondLine);

  if (!result) return null;

  const { stockMessage, availabilityMessage, stockLevelMessage, stockLevel } =
    result;

  if (!availabilityMessage) return null;

  const chooseStore = () => {
    setLocationAndFavouriteStore(favouriteStoreDetails.data.geo);
    openStockLevelModal();
  };

  return (
    <div>
      <span
        className={cn(styles.availabilityText, {
          [styles.availabilityTextInline]: !selectedAvailability,
        })}
      >
        {FEATURE_FLAG_DISABLE_SPU ? (
          stockMessage
        ) : (
          <div className={styles.availabilityMessageContainer}>
            <div>
              {parsedMessage ? (
                <>
                  <span>{parsedMessage.beforeStore}</span>
                  {parsedMessage.storeName && (
                    <>
                      {' '}
                      <SimpleButton
                        variant="secondary"
                        mode="light"
                        className={styles.storeNameButton}
                        onClick={chooseStore}
                      >
                        {parsedMessage.storeName}
                      </SimpleButton>
                    </>
                  )}
                  {parsedMessage.afterStore && (
                    <>
                      {/* No space before punctuation, as in "Halifax." */}
                      {!/^[.,]/.test(parsedMessage.afterStore) && ' '}
                      <span>{parsedMessage.afterStore}</span>
                    </>
                  )}
                  {(parsedMessage.secondLine ||
                    parsedMessage.secondLineLink) && (
                    <span className={styles.secondLine}>
                      {parsedMessage.secondLineLink ? (
                        <>
                          <SimpleButton
                            variant="secondary"
                            mode="light"
                            className={styles.storeNameButton}
                            onClick={chooseStore}
                          >
                            {parsedMessage.secondLineLink}
                            {secondLine.punctuation && (
                              <span className={styles.linkPunctuation}>
                                {secondLine.punctuation}
                              </span>
                            )}
                          </SimpleButton>
                          {secondLine.rest && ` ${secondLine.rest}`}
                        </>
                      ) : (
                        parsedMessage.secondLine
                      )}
                    </span>
                  )}
                </>
              ) : (
                <>
                  <Skeleton
                    height={14}
                    width={180}
                    className={styles.availabilitySkeleton}
                  />
                  <Skeleton
                    height={14}
                    width={180}
                    className={styles.availabilitySkeleton}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <FulfillmentStockLevelRow
          stockLevel={stockLevel}
          stockLevelMessage={stockLevelMessage}
          fitContent
        />
      </span>
    </div>
  );
};

export default StoreAvailabilityMessage;
